import type { AppRole } from "@/lib/roles";
import { NAV_DOMAINS, type NavIcon } from "@/lib/navigation";

/**
 * VX-2 universal command palette model.
 *
 * The palette is a navigation surface only: every command resolves to a route
 * the signed-in role is already permitted to reach through the rail. It never
 * performs a privileged action, never fabricates a destination, and never
 * exposes a route outside the caller's role scope — RLS and route guards
 * remain the authority; this is purely a faster way to reach them.
 */

export interface CommandItem {
  id: string;
  label: string;
  to: string;
  /** Grouping heading shown in the palette. */
  group: string;
  icon: NavIcon;
  /** Extra search terms so operators can type what they mean. */
  keywords: string[];
  /** Role destinations rank above contextual children. */
  tier: "primary" | "secondary";
}

const visibleTo = (roles: AppRole[] | "all", role: AppRole | null) =>
  roles === "all" || (!!role && roles.includes(role));

/**
 * Additional search terms per destination, so "quote", "price" or "new load"
 * all find the booking flow. Terms are keyed by route.
 */
const ROUTE_KEYWORDS: Record<string, string[]> = {
  "/app/shipper/create": ["book", "new load", "quote", "price", "cbr", "shipment"],
  "/app/shipper/shipments": ["loads", "orders", "history", "status"],
  "/app/transporter/opportunities": ["offers", "loads", "bids", "work"],
  "/app/transporter/fleet": ["vehicles", "trucks", "drivers"],
  "/app/transporter/availability": ["capacity", "empty", "post truck"],
  "/app/transporter/return-journey": ["backhaul", "return", "empty leg"],
  "/app/driver/scan": ["barcode", "pickup", "qr"],
  "/app/driver/mobile/pod": ["proof of delivery", "photo", "signature"],
  "/app/driver/mobile/tracking": ["gps", "location", "map"],
  "/app/settings": ["profile", "account", "preferences"],
  "/app/security": ["mfa", "password", "sessions", "devices"],
  "/app/documents": ["files", "uploads", "verification"],
  "/app/support": ["help", "contact", "messages"],
  "/app/notifications": ["alerts", "inbox"],
  "/app/intelligence": ["ai", "insights", "recommendations"],
  "/app/founder": ["cockpit", "command centre", "now"],
  "/app/founder/switches": ["kill switch", "pause", "safety"],
  "/app/founder/pricing-centre": ["mipe", "cbr", "tariff", "rates"],
  "/app/admin": ["ops", "command centre"],
  "/app/admin/finance": ["invoices", "payouts", "payments"],
  "/app/admin/compliance": ["verification", "documents", "risk"],
};

const norm = (s: string) => s.toLowerCase().trim();

/** All commands the given role may reach, derived from the nav domain model. */
export function commandsFor(role: AppRole | null): CommandItem[] {
  const items: CommandItem[] = [];
  const seen = new Set<string>();

  for (const domain of NAV_DOMAINS) {
    if (!visibleTo(domain.roles, role)) continue;
    const push = (to: string, label: string, icon: NavIcon, tier: CommandItem["tier"]) => {
      if (seen.has(to)) return;
      seen.add(to);
      items.push({
        id: `${domain.id}:${to}`,
        label,
        to,
        group: domain.label,
        icon,
        keywords: [...(ROUTE_KEYWORDS[to.split("?")[0]] ?? []), norm(domain.label)],
        tier,
      });
    };
    push(domain.to, domain.label, domain.icon, "primary");
    for (const child of domain.children) push(child.to, child.label, child.icon, "secondary");
  }

  return items;
}

/**
 * Ranks commands against a query. Exact label prefix beats label substring,
 * which beats a keyword hit; role destinations break ties. An empty query
 * returns the role destinations only, so the palette opens on "where can I go".
 */
export function searchCommands(items: CommandItem[], query: string, limit = 12): CommandItem[] {
  const q = norm(query);
  if (!q) return items.filter((i) => i.tier === "primary").slice(0, limit);

  const scored = items
    .map((item) => {
      const label = norm(item.label);
      let score = 0;
      if (label === q) score = 100;
      else if (label.startsWith(q)) score = 80;
      else if (label.includes(q)) score = 60;
      else if (item.keywords.some((k) => k.includes(q))) score = 40;
      else if (norm(item.group).startsWith(q)) score = 20;
      if (score && item.tier === "primary") score += 5;
      return { item, score };
    })
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score || a.item.label.localeCompare(b.item.label));

  return scored.slice(0, limit).map((s) => s.item);
}

/** Group ordering preserved from the nav model, for rendering. */
export function groupCommands(items: CommandItem[]): { group: string; items: CommandItem[] }[] {
  const order: string[] = [];
  const map = new Map<string, CommandItem[]>();
  for (const item of items) {
    if (!map.has(item.group)) { map.set(item.group, []); order.push(item.group); }
    map.get(item.group)!.push(item);
  }
  return order.map((group) => ({ group, items: map.get(group)! }));
}

/** True when the keyboard event should open the palette (⌘K / Ctrl+K). */
export function isPaletteShortcut(e: Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey">): boolean {
  return norm(e.key) === "k" && (e.metaKey || e.ctrlKey);
}
